import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { freightAPI } from '../api';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  DocumentIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  ShareIcon,
  ArrowPathIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { TFSLoader } from '../components/ui';
import UTIF from 'utif';
import JSZip from 'jszip';

const SUPPORTED_ATTACHMENT_EXTENSIONS = ['.pdf', '.tif', '.tiff', '.png', '.jpg', '.jpeg', '.jfif', '.gif'];

const isSupportedAttachment = (filename = '') => (
  SUPPORTED_ATTACHMENT_EXTENSIONS.some((ext) => filename.toLowerCase().endsWith(ext))
);

const getAttachmentName = (attachment) => {
  if (!attachment) return 'attachment';
  if (attachment.name) return attachment.name;
  if (attachment.filename) return attachment.filename;
  if (attachment.blob_name) return attachment.blob_name.split('/').pop();
  if (attachment.storage?.blob_name) return attachment.storage.blob_name.split('/').pop();
  return 'attachment';
};

const getAttachmentUrl = (attachment) => (
  attachment?.download_url
  || attachment?.blob_url
  || attachment?.storage?.download_url
  || attachment?.storage?.blob_url
);

const normalizeAttachmentPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return [];

  const base64Attachments = Object.entries(payload.pdfs || {}).map(([filename, base64Content]) => ({
    name: filename,
    base64Content,
  }));

  const metadataAttachments = Array.isArray(payload.attachments_data?.attachments)
    ? payload.attachments_data.attachments
    : Array.isArray(payload.attachments)
      ? payload.attachments
      : [];

  const urlAttachments = metadataAttachments.map((attachment) => ({
    name: getAttachmentName(attachment),
    base64Content: attachment.base64Content || attachment.content,
    url: getAttachmentUrl(attachment),
    contentType: attachment.content_type || attachment.storage?.content_type,
    size: attachment.size || attachment.storage?.size,
  }));

  return [...base64Attachments, ...urlAttachments].filter((attachment) => {
    const name = attachment.name || '';
    const hasContent = attachment.base64Content || attachment.url;
    return hasContent && (isSupportedAttachment(name) || name.toLowerCase().endsWith('.zip'));
  });
};

const expandAttachmentArchives = async (attachments, onZipError) => {
  const expanded = [];

  for (const attachment of attachments) {
    if (attachment.name.toLowerCase().endsWith('.zip') && attachment.base64Content) {
      try {
        const cleanedZip = (attachment.base64Content || '').replace(/^"|"$/g, '').replace(/\s+/g, '');
        const zip = await JSZip.loadAsync(cleanedZip, { base64: true });
        const files = Object.values(zip.files);
        for (const file of files) {
          if (file.dir) continue;
          const baseName = file.name.split('/').pop();
          if (!isSupportedAttachment(baseName)) continue;
          const innerBase64 = await file.async('base64');
          expanded.push({
            name: baseName,
            base64Content: innerBase64,
            group: attachment.name,
          });
        }
      } catch (zipErr) {
        onZipError();
      }
    } else if (isSupportedAttachment(attachment.name)) {
      expanded.push(attachment);
    }
  }

  return expanded;
};

const RequestDetail = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reprocessChain, setReprocessChain] = useState([]);

  // Tab and view state
  const [activeTab, setActiveTab] = useState('overview');
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side', 'pdf-only', 'data-only'
  const [selectedPdf, setSelectedPdf] = useState(0); // Which PDF to show (0 or 1)
  
  // PDF attachments state
  const [pdfAttachments, setPdfAttachments] = useState([]);
  const [pdfAttachmentsLoading, setPdfAttachmentsLoading] = useState(false);



  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState([]);
  const [editedAddresses, setEditedAddresses] = useState(null);

  // Search/filter state
  const [searchTerm, setSearchTerm] = useState('');

  // Button-level loading states
  const [loadingSendExcel, setLoadingSendExcel] = useState(false);
  const [loadingDownloadExcel, setLoadingDownloadExcel] = useState(false);
  const [loadingDownloadXml, setLoadingDownloadXml] = useState(false);
  const [loadingSendXml, setLoadingSendXml] = useState(false);
  const [loadingReprocess, setLoadingReprocess] = useState(false);
  
  // Reprocess cooldown state
  const [reprocessCooldown, setReprocessCooldown] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Create the default layout plugin at the top level
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Check for existing cooldown on mount
  useEffect(() => {
    const cooldownKey = `reprocess_cooldown_${requestId}`;
    const storedCooldown = localStorage.getItem(cooldownKey);
    if (storedCooldown) {
      const cooldownTime = parseInt(storedCooldown);
      const now = Date.now();
      if (now < cooldownTime) {
        setReprocessCooldown(cooldownTime);
      } else {
        localStorage.removeItem(cooldownKey);
      }
    }
  }, [requestId]);

  // Update cooldown timer
  useEffect(() => {
    if (!reprocessCooldown) {
      setCooldownRemaining(0);
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((reprocessCooldown - now) / 1000));
      setCooldownRemaining(remaining);

      if (remaining === 0) {
        setReprocessCooldown(null);
        localStorage.removeItem(`reprocess_cooldown_${requestId}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [reprocessCooldown, requestId]);

  // Tiny spinner for inline button usage
  const SmallSpinner = ({ className = 'h-4 w-4' }) => (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-white/40 border-t-white ${className}`}
      aria-hidden="true"
    />
  );

  const TiffViewer = ({ base64, zoom = 1 }) => {
    const canvasRef = useRef(null);
    const offscreenRef = useRef(null);
    const dimsRef = useRef({ width: 0, height: 0 });

    const draw = () => {
      const canvas = canvasRef.current;
      const off = offscreenRef.current;
      const { width, height } = dimsRef.current;
      if (!canvas || !off || !width || !height) return;
      const parent = canvas.parentElement || canvas;
      const parentRect = parent.getBoundingClientRect();
      const availableWidth = Math.max(1, parentRect.width);
      const availableHeight = Math.max(1, parentRect.height);
      const baseFit = Math.min(availableWidth / width, availableHeight / height);
      const finalScale = Math.max(0.05, baseFit * zoom);
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(width * dpr * finalScale));
      canvas.height = Math.max(1, Math.floor(height * dpr * finalScale));
      canvas.style.width = `${Math.max(1, Math.floor(width * finalScale))}px`;
      canvas.style.height = `${Math.max(1, Math.floor(height * finalScale))}px`;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(off, 0, 0, width, height, 0, 0, canvas.width, canvas.height);
    };

    useEffect(() => {
      try {
        if (!base64) return;
        const cleaned = base64.replace(/^"|"$/g, '').replace(/\s+/g, '');
        const binary = atob(cleaned);
        const buffer = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) buffer[i] = binary.charCodeAt(i);
        const ifds = UTIF.decode(buffer);
        if (!ifds || ifds.length === 0) return;
        UTIF.decodeImage(buffer, ifds[0]);
        const rgba = UTIF.toRGBA8(ifds[0]);
        const width = ifds[0].width || ifds[0].t256 || 0;
        const height = ifds[0].height || ifds[0].t257 || 0;
        const off = document.createElement('canvas');
        off.width = width;
        off.height = height;
        const offCtx = off.getContext('2d');
        const imageData = offCtx.createImageData(width, height);
        imageData.data.set(rgba);
        offCtx.putImageData(imageData, 0, 0);
        offscreenRef.current = off;
        dimsRef.current = { width, height };
        draw();
        // Re-render on resize
        let ro;
        const parent = canvasRef.current?.parentElement;
        if (parent && 'ResizeObserver' in window) {
          ro = new ResizeObserver(() => draw());
          ro.observe(parent);
        } else {
          const onResize = () => draw();
          window.addEventListener('resize', onResize);
          return () => window.removeEventListener('resize', onResize);
        }
        return () => {
          if (ro) ro.disconnect();
        };
      } catch (err) {
        toast.error('Failed to render TIFF');
        }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base64]);

    useEffect(() => {
      draw();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoom]);

    return <canvas ref={canvasRef} className="block" />;
  };

  // Zoom for image/TIFF attachments
  const [attachmentZoom, setAttachmentZoom] = useState(1);
  useEffect(() => {
    setAttachmentZoom(1);
  }, [selectedPdf]);

  const fetchRequestData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const response = await freightAPI.getRequest(requestId);
      setData(response);
      setError(null);
    } catch (err) {
      if (!silent) {
        toast.error('Failed to fetch request detail');
      }
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  const fetchReprocessChain = useCallback(async () => {
    try {
      const response = await freightAPI.getReprocessChain(requestId);
      setReprocessChain(Array.isArray(response.items) ? response.items : []);
    } catch (err) {
      setReprocessChain([]);
    }
  }, [requestId]);

  useEffect(() => {
    fetchRequestData();
    fetchReprocessChain();
  }, [fetchRequestData, fetchReprocessChain]);

  const latestAttempt = useMemo(() => {
    const currentAttemptNumber = Number(data?.attempt_number || 1);
    const attempts = reprocessChain
      .filter((item) => item?.request_id && item.request_id !== requestId)
      .filter((item) => Number(item.attempt_number || 1) > currentAttemptNumber)
      .sort((a, b) => Number(a.attempt_number || 1) - Number(b.attempt_number || 1));

    return attempts.length > 0 ? attempts[attempts.length - 1] : null;
  }, [data?.attempt_number, reprocessChain, requestId]);

  const isReprocessWaiting = ['queued', 'processing'].includes(data?.reprocess_status);

  useEffect(() => {
    if (!isReprocessWaiting) return undefined;

    const interval = setInterval(() => {
      fetchRequestData({ silent: true });
      fetchReprocessChain();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchRequestData, fetchReprocessChain, isReprocessWaiting]);

  // Fetch PDF attachments
  useEffect(() => {
    const fetchPdfAttachments = async () => {
      if (!requestId || !data) return;
      
      setPdfAttachmentsLoading(true);
      try {
        const response = await freightAPI.getEmailPdfAttachments(requestId);
        const endpointAttachments = await expandAttachmentArchives(
          normalizeAttachmentPayload(response),
          () => toast.error('Failed to extract ZIP attachment')
        );
        const requestAttachments = await expandAttachmentArchives(
          normalizeAttachmentPayload(data),
          () => toast.error('Failed to extract ZIP attachment')
        );

        setPdfAttachments(endpointAttachments.length > 0 ? endpointAttachments : requestAttachments);
      } catch (err) {
        const requestAttachments = await expandAttachmentArchives(
          normalizeAttachmentPayload(data),
          () => toast.error('Failed to extract ZIP attachment')
        );

        if (requestAttachments.length > 0) {
          setPdfAttachments(requestAttachments);
        } else {
          toast.error('Failed to fetch PDF attachments');
          setPdfAttachments([]);
        }
      } finally {
        setPdfAttachmentsLoading(false);
      }
    };

    fetchPdfAttachments();
  }, [requestId, data]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      pdfAttachments.forEach(attachment => {
        if (attachment.url && attachment.url.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.url);
        }
      });
    };
  }, [pdfAttachments]);



  const handleEdit = () => {
    setEditedData(JSON.parse(JSON.stringify(structuredPoItems)));
    setEditedAddresses(JSON.parse(JSON.stringify(data.addresses || {})));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedData([]);
    setEditedAddresses(null);
  };

  const handleInputChange = (rowIdx, col, value) => {
    setEditedData(prev => {
      const updated = [...prev];
      if (updated[rowIdx] && updated[rowIdx][col]) {
        updated[rowIdx][col].value = value;
      }
      return updated;
    });
  };

  const handleSaveEdit = async () => {
    const updates = {};
    
    if (editedData && Array.isArray(editedData)) {
      updates.structured_po_items = editedData;
    }
    
    if (editedAddresses && typeof editedAddresses === 'object') {
      updates.addresses = editedAddresses;
    }

    if (Object.keys(updates).length === 0) {
      setIsEditing(false);
      return;
    }

    try {
      await freightAPI.updateRequest(requestId, updates);
      
      const response = await freightAPI.getRequest(requestId);
      setData(response);
      toast.success('Data updated successfully');

      setIsEditing(false);
      setEditedData([]);
      setEditedAddresses(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save data. Please try again.');
    }
  };

  const handleAddressChange = (field, value) => {
    setEditedAddresses(prev => ({ ...(prev || {}), [field]: value }));
  };

  const handleSendExcelFile = async () => {
    setLoadingSendExcel(true);
    try {
      await freightAPI.generateExcelAndSend(requestId);
      toast.success('Excel file generated and sent successfully');
    } catch (err) {
      toast.error('Failed to send Excel file');
    } finally {
      setLoadingSendExcel(false);
    }
  };

  const handleDownloadExcelFile = async () => {
    setLoadingDownloadExcel(true);
    try {
      // 1. Define static headers
      const staticHeaders = [
        'Shipper Address',
        'Consignee Address',
        'POL',
        'POD',
        'ThermoFisher Code',
        'External Code'
      ];

      // 2. Get all dynamic columns from structured_po_items
      const dynamicColumns = structuredColumns.filter(
        col => !['_id'].includes(col)
      );

      // 3. Build the full header row
      const headers = [...staticHeaders, ...dynamicColumns];

      // 4. Map each PO item to a row
      const rows = (structuredPoItems || []).map(row => {
        const base = [
          data.addresses?.shipper || '',
          data.addresses?.consignee || '',
          data.addresses?.pol || '',
          data.addresses?.pod || '',
          data.addresses?.thermo_fisher_code || '',
          data.addresses?.external_code || '',
        ];
        // Add dynamic columns (skip static ones)
        const dynamicValues = dynamicColumns.map(col => {
          // Avoid duplicating static fields
          if ([
            'Shipper Address',
            'Consignee Address',
            'POL',
            'POD',
            'ThermoFisher Code',
            'External Code'
          ].includes(col)) return undefined;
          const field = row[col];
          return field && typeof field === 'object' ? field.value : field;
        });
        return [...base, ...dynamicValues];
      });

      // 5. Create worksheet and workbook
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'PO Items');

      // 6. Trigger download
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      const excelFileName = `TFS_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
      XLSX.writeFile(workbook, excelFileName);
    } catch (err) {
      alert('Failed to download Excel file');
    } finally {
      setLoadingDownloadExcel(false);
    }
  };

  const handleSendXmlToFtp = async () => {
    setLoadingSendXml(true);
    try {
      await freightAPI.generateXmlAndSendToFtp(requestId);
      toast.success('XML generated and sent to Smart Order successfully');
      // Refresh the page data
      const response = await freightAPI.getRequest(requestId);
      setData(response);
    } catch (err) {
      toast.error('Failed to send XML to Smart Order');
    } finally {
      setLoadingSendXml(false);
    }
  };

  const handleDownloadXmlFile = async () => {
    setLoadingDownloadXml(true);
    try {
      const xmlText = await freightAPI.generateRequestXml(requestId);
      if (!xmlText || typeof xmlText !== 'string') {
        throw new Error('No XML content returned');
      }
      const blob = new Blob([xmlText], { type: 'application/xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      const fileName = `TFS_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xml`;
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('XML file downloaded');
      // Refresh the page data
      const response = await freightAPI.getRequest(requestId);
      setData(response);
    } catch (err) {
      toast.error('Failed to download XML file');
    } finally {
      setLoadingDownloadXml(false);
    }
  };

  const handleReprocessRequest = async () => {
    if (reprocessCooldown) {
      toast.warning(`Please wait ${cooldownRemaining} seconds before reprocessing again`);
      return;
    }

    setLoadingReprocess(true);
    try {
      const response = await freightAPI.reprocessRequest(requestId);
      const attemptLabel = response.next_attempt_number ? ` Attempt ${response.next_attempt_number}` : '';
      toast.success(`Reprocess queued.${attemptLabel} will get a new request ID.`);
      await fetchRequestData({ silent: true });
      await fetchReprocessChain();
      
      // Set 5-minute cooldown
      const cooldownTime = Date.now() + (5 * 60 * 1000);
      setReprocessCooldown(cooldownTime);
      localStorage.setItem(`reprocess_cooldown_${requestId}`, cooldownTime.toString());
    } catch (err) {
      toast.error(err.message || 'Failed to mark request for reprocessing');
    } finally {
      setLoadingReprocess(false);
    }
  };

  // Function to render attachment content (PDF, TIFF, Images)
  const renderAttachmentContent = () => {
    if (pdfAttachmentsLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
            <p className="text-gray-500">Loading attachments...</p>
          </div>
        </div>
      );
    }
    
    if (pdfAttachments.length === 0) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No attachments available</p>
            <p className="text-sm text-gray-400">This request has no attached documents</p>
          </div>
        </div>
      );
    }
    
    const selectedAttachment = pdfAttachments[selectedPdf];
    
    if (!selectedAttachment || (!selectedAttachment.base64Content && !selectedAttachment.url)) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Attachment content not available</p>
          </div>
        </div>
      );
    }

    const cleanedB64 = selectedAttachment.base64Content
      ? selectedAttachment.base64Content.replace(/^"|"$/g, '').replace(/\s+/g, '')
      : '';
    const fileSizeBytes = selectedAttachment.size || (cleanedB64 ? (cleanedB64.length * 3) / 4 : 0);
    const fileSizeLabel = fileSizeBytes ? `${(fileSizeBytes / (1024 * 1024)).toFixed(2)}MB` : 'unknown size';

    const nameLower = (selectedAttachment.name || '').toLowerCase();
    const contentType = (selectedAttachment.contentType || '').toLowerCase();
    const isPdf = nameLower.endsWith('.pdf') || contentType === 'application/pdf';
    const isTiff = nameLower.endsWith('.tif') || nameLower.endsWith('.tiff') || contentType === 'image/tiff';
    const isPng = nameLower.endsWith('.png') || contentType === 'image/png';
    const isJpeg = nameLower.endsWith('.jpg') || nameLower.endsWith('.jpeg') || nameLower.endsWith('.jfif') || contentType === 'image/jpeg';
    const isGif = nameLower.endsWith('.gif') || contentType === 'image/gif';

    const dataUrl = cleanedB64 && isPdf
      ? `data:application/pdf;base64,${cleanedB64}`
      : cleanedB64 && isTiff
      ? `data:image/tiff;base64,${cleanedB64}`
      : cleanedB64 && isPng
      ? `data:image/png;base64,${cleanedB64}`
      : cleanedB64 && isJpeg
      ? `data:image/jpeg;base64,${cleanedB64}`
      : cleanedB64 && isGif
      ? `data:image/gif;base64,${cleanedB64}`
      : undefined;
    const previewUrl = dataUrl || selectedAttachment.url;

    const downloadAttachment = () => {
      if (!previewUrl) return;
      const link = document.createElement('a');
      link.href = previewUrl;
      link.download = selectedAttachment.name;
      if (selectedAttachment.url) link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const pdfDataUrl = isPdf ? previewUrl : undefined;

    return (
      <div className="space-y-4">
        {/* File info and controls */}
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              File: {selectedAttachment.group ? `${selectedAttachment.group} / ` : ''}{selectedAttachment.name} ({fileSizeLabel})
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Zoom controls for images/TIFFs */}
            {!isPdf && (
              <div className="inline-flex items-center bg-white border border-gray-300 rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAttachmentZoom(z => Math.max(0.25, +(z - 0.25).toFixed(2)))}
                  className="px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                  title="Zoom out"
                >
                  -
                </button>
                <span className="px-2 py-1 text-xs text-gray-600 border-l border-r border-gray-200 w-16 text-center">
                  {Math.round(attachmentZoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setAttachmentZoom(z => Math.min(5, +(z + 0.25).toFixed(2)))}
                  className="px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                  title="Zoom in"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => setAttachmentZoom(1)}
                  className="px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 border-l border-gray-200"
                  title="Reset zoom"
                >
                  Reset
                </button>
              </div>
            )}
            {/* Download button */}
            <button
              onClick={downloadAttachment}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download
            </button>
          </div>
        </div>

        {/* Attachment Viewer */}
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" style={{ height: '600px' }}>
          {isPdf && pdfDataUrl && dataUrl && (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                fileUrl={pdfDataUrl}
                plugins={[defaultLayoutPluginInstance]}
                defaultScale={1.0}
              />
            </Worker>
          )}
          {isPdf && pdfDataUrl && selectedAttachment.url && !dataUrl && (
            <iframe
              src={pdfDataUrl}
              title={selectedAttachment.name}
              className="w-full h-full border-0"
            />
          )}
          {!isPdf && (isPng || isJpeg || isGif) && previewUrl && (
            <div className="w-full h-full p-4 overflow-auto">
              <div
                style={{ transform: `scale(${attachmentZoom})`, transformOrigin: 'top left' }}
              >
                <img
                  src={previewUrl}
                  alt={selectedAttachment.name}
                  className="w-[calc(100%-0px)] h-[calc(100%-0px)] object-contain max-w-full max-h-full"
                  style={{ display: 'block' }}
                />
              </div>
            </div>
          )}
          {!isPdf && isTiff && cleanedB64 && (
            <div className="w-full h-full flex items-start justify-start p-4 overflow-auto">
              <TiffViewer base64={cleanedB64} zoom={attachmentZoom} />
            </div>
          )}
          {!isPdf && !(isTiff && cleanedB64) && !(isPng || isJpeg || isGif) && (
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Unsupported file type</p>
                <p className="text-sm text-gray-400">Cannot preview this attachment. Try downloading it.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <TFSLoader />;
  }

  if (error) {
    return <div className="text-red-600 font-medium">{error}</div>;
  }

  if (!data) {
    return null;
  }

  const ConfidenceBadge = ({ confidence }) => {
    if (!confidence) return <span className="ml-2 px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 border border-yellow-200">MEDIUM</span>;
    let color = 'bg-gray-100 text-gray-600 border-gray-200';
    if (confidence === 'HIGH') color = 'bg-green-100 text-green-700 border-green-200';
    else if (confidence === 'MEDIUM') color = 'bg-yellow-100 text-yellow-700 border-yellow-200';
    else if (confidence === 'LOW') color = 'bg-red-100 text-red-700 border-red-200';
    return (
      <span className={`ml-2 px-2.5 py-1 rounded-full text-xs font-semibold border ${color} shadow-sm`}>{confidence}</span>
    );
  };

  const structuredPoItems = data.structured_po_items || [];
  const structuredColumns = structuredPoItems.length > 0 ? Object.keys(structuredPoItems[0]).filter((col) => col !== '_id' && col !== 'pdf_path') : [];

  // Courier packagings live inside data.po_items per PDF: po_items[filename].courier_packagings
  const courierPackagings = (data.po_items && typeof data.po_items === 'object')
    ? Object.values(data.po_items).flatMap((po) => (Array.isArray(po?.courier_packagings) ? po.courier_packagings : []))
    : [];
  const awbNumbers = courierPackagings
    .map((pkg) => pkg && pkg.awb_number)
    .filter(Boolean);
  const primaryAwb = awbNumbers[0] || null;
  const extraAwbCount = awbNumbers.length > 1 ? awbNumbers.length - 1 : 0;
  // Filter function for search and PDF filter
  const filterStructuredData = (dataArray) => {
    let filtered = dataArray;
    
    // Filter by PDF path if a specific PDF is selected
    if (pdfAttachments.length > 0 && selectedPdf < pdfAttachments.length) {
      const selectedAttachment = pdfAttachments[selectedPdf];
      const selectedBaseName = (selectedAttachment?.name || '').split(/[:/\\]/).pop();
      filtered = filtered.filter(row => {
        const pdfPathField = row.pdf_path;
        const rowBaseName = pdfPathField && pdfPathField.value ? String(pdfPathField.value).split(/[:/\\]/).pop() : undefined;
        return rowBaseName && rowBaseName === selectedBaseName;
      });
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        Object.values(row).some(
          field => field && field.value && field.value.toString().toLowerCase().includes(searchLower)
        )
      );
    }
    
    return filtered;
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: EyeIcon },
    { id: 'documents', name: 'Documents & Data', icon: DocumentIcon },
    { id: 'mapping', name: 'Mapping Details', icon: TableCellsIcon },
  ];

  const isCourier = (data.load_type || '').toLowerCase() === 'courier';

  const renderOverviewTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Address Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">Address Summary</h2>
              {data.load_type && (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                  isCourier ? 'bg-sky-100 text-sky-800 border border-sky-200' : 'bg-gray-100 text-gray-700 border border-gray-200'
                }`}>
                  {isCourier && <TruckIcon className="h-3.5 w-3.5 mr-1" />}
                  Load: {String(data.load_type)}
                </span>
              )}
            </div>
            <button
              onClick={handleReprocessRequest}
              disabled={loadingReprocess || !!reprocessCooldown}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg ${
                loadingReprocess || reprocessCooldown
                  ? 'bg-orange-400 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
              title={reprocessCooldown ? `Wait ${cooldownRemaining}s before creating another attempt` : 'Create a new processing attempt from the original email'}
            >
              {loadingReprocess ? (
                <><SmallSpinner className="h-4 w-4 mr-2" /> Queueing...</>
              ) : reprocessCooldown ? (
                <><ArrowPathIcon className="h-4 w-4 mr-1" /> Wait {Math.floor(cooldownRemaining / 60)}:{String(cooldownRemaining % 60).padStart(2, '0')}</>
              ) : (
                <><ArrowPathIcon className="h-4 w-4 mr-1" /> New Attempt</>
              )}
            </button>
          </div>
          <div className="p-6 space-y-4">
            {/* Top row: shipper / consignee names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-indigo-50/40 p-4">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Shipper</span>
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedAddresses?.shipper ?? data.addresses?.shipper ?? ''}
                    onChange={(e) => handleAddressChange('shipper', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter shipper"
                  />
                ) : (
                  <>
                    <div className="text-sm font-medium text-gray-900 break-words">
                      {data.addresses?.shipper ?? 'N/A'}
                    </div>
                    {primaryAwb && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <span className="font-semibold">AWB:</span>
                        <span className="inline-flex items-center rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 font-mono text-[11px] text-sky-800">
                          {primaryAwb}
                        </span>
                        {extraAwbCount > 0 && (
                          <span className="text-[11px] text-gray-500">+{extraAwbCount} more</span>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div className="mt-4 border-t border-indigo-100 pt-3">
                  <span className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">Shipper Code</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedAddresses?.supplier_thermofisher_code ?? data.addresses?.supplier_thermofisher_code ?? ''}
                      onChange={(e) => handleAddressChange('supplier_thermofisher_code', e.target.value)}
                      className="mt-1 border border-gray-300 rounded-md px-3 py-2 text-xs w-full font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter shipper code"
                    />
                  ) : (
                    <div className="mt-1 text-xs font-mono text-gray-800 break-all">
                      {data.addresses?.supplier_thermofisher_code || 'N/A'}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-emerald-50/40 p-4">
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5 block">
                  Consignee
                </span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedAddresses?.consignee ?? data.addresses?.consignee ?? ''}
                    onChange={(e) => handleAddressChange('consignee', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter consignee"
                  />
                ) : (
                  <div className="text-sm font-medium text-gray-900 break-words">
                    {data.addresses?.consignee ?? 'N/A'}
                  </div>
                )}
                <div className="mt-4 border-t border-emerald-100 pt-3">
                  <span className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">Consignee Code</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedAddresses?.consignee_thermofisher_code ?? data.addresses?.consignee_thermofisher_code ?? ''}
                      onChange={(e) => handleAddressChange('consignee_thermofisher_code', e.target.value)}
                      className="mt-1 border border-gray-300 rounded-md px-3 py-2 text-xs w-full font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter consignee code"
                    />
                  ) : (
                    <div className="mt-1 text-xs font-mono text-gray-800 break-all">
                      {data.addresses?.consignee_thermofisher_code || 'N/A'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Middle row: addresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Shipper Address
                </div>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={editedAddresses?.supplier_address ?? data.addresses?.supplier_address ?? ''}
                    onChange={(e) => handleAddressChange('supplier_address', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full resize-y focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter shipper address"
                  />
                ) : (
                  <div className="text-sm text-gray-900 break-words">
                    {data.addresses?.supplier_address ?? 'N/A'}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                  Consignee Address
                </div>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={editedAddresses?.consignee_address ?? data.addresses?.consignee_address ?? ''}
                    onChange={(e) => handleAddressChange('consignee_address', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full resize-y focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter consignee address"
                  />
                ) : (
                  <div className="text-sm text-gray-900 break-words">
                    {data.addresses?.consignee_address ?? 'N/A'}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom row: POL / POD chips */}
            <div className="flex flex-wrap gap-3">
              <div className="inline-flex flex-col">
                <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">POL</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedAddresses?.pol ?? data.addresses?.pol ?? ''}
                    onChange={(e) => handleAddressChange('pol', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-xs w-32 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="POL"
                  />
                ) : (
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-800">
                    {data.addresses?.pol ?? 'N/A'}
                  </span>
                )}
              </div>
              <div className="inline-flex flex-col">
                <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">POD</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedAddresses?.pod ?? data.addresses?.pod ?? ''}
                    onChange={(e) => handleAddressChange('pod', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-xs w-32 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="POD"
                  />
                ) : (
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-800">
                    {data.addresses?.pod ?? 'N/A'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Processing Time Information */}
        {(data.start_time || data.end_time || data.duration_seconds) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center justify-center w-9 h-9 bg-blue-600 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Processing Timeline</h2>
            </div>
            <div className="p-6 grid grid-cols-1 gap-3">
              {data.start_time && (
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Start Time (UTC)</div>
                  <div className="text-base font-semibold text-gray-900">{data.start_time.replace('T', ' ').split('.')[0]}</div>
                </div>
              )}
              {data.end_time && (
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">End Time (UTC)</div>
                  <div className="text-base font-semibold text-gray-900">{data.end_time.replace('T', ' ').split('.')[0]}</div>
                </div>
              )}
              {data.duration_seconds && (
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Duration</div>
                  <div className="text-base font-semibold text-gray-900">
                    {Math.floor(data.duration_seconds / 60)}m {Math.round(data.duration_seconds % 60)}s
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Data Preview */}
      {structuredPoItems && structuredPoItems.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Data Preview</h2>
          </div>
          <div className="p-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {structuredColumns.slice(0, 5).map((key) => (
                    <th key={key} className="px-4 py-2.5 text-left font-medium text-gray-700 rounded-t-lg">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {structuredPoItems.slice(0, 3).map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50">
                    {structuredColumns.slice(0, 5).map((col) => {
                      const field = row[col];
                      const value = field && typeof field === 'object' ? field.value : field;
                      if (value === null || value === undefined || value === '') {
                        return <td key={col} className="px-4 py-2.5 whitespace-nowrap text-gray-400">—</td>;
                      }
                      return (
                        <td key={col} className="px-4 py-2.5 whitespace-nowrap text-gray-900">
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {structuredPoItems.length > 3 && (
              <p className="text-sm text-gray-500 mt-3">
                Showing 3 of {structuredPoItems.length} rows. View full data in Documents & Data tab.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Data Preview</h2>
          </div>
          <div className="p-6 text-center py-10 text-gray-500">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p>No mapped data available</p>
            <p className="text-sm text-gray-400 mt-1">
              {data.po_items ? 'No items found in po_items data' : 'No po_items data available for this request'}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderDocumentsTab = () => (
    <div className="space-y-4">
      {/* View Mode Controls */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">View Mode:</span>
          <div className="flex rounded-md shadow-sm">
            {[
              { id: 'side-by-side', label: 'Side by Side', icon: '⚖️' },
              { id: 'pdf-only', label: 'PDF Only', icon: '📄' },
              { id: 'data-only', label: 'Data Only', icon: '📊' },
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                className={`px-3 py-2 text-sm font-medium border ${
                  viewMode === mode.id
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                } ${mode.id === 'side-by-side' ? 'rounded-l-md' : mode.id === 'data-only' ? 'rounded-r-md' : ''}`}
              >
                {mode.icon} {mode.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Search input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-3 py-1.5 border border-gray-300 rounded-md text-sm w-48 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Document:</span>
            {pdfAttachmentsLoading ? (
              <div className="text-sm text-gray-500">Loading documents...</div>
            ) : pdfAttachments.length > 0 ? (
              <select
                value={selectedPdf}
                onChange={(e) => setSelectedPdf(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                {pdfAttachments.map((pdf, index) => (
                  <option key={index} value={index}>
                    {pdf.group ? `${pdf.group} / ${pdf.name}` : pdf.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-sm text-gray-500">No documents available</div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className={`grid gap-6 ${viewMode === 'side-by-side' ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* PDF Viewer */}
        {(viewMode === 'side-by-side' || viewMode === 'pdf-only') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {pdfAttachments[selectedPdf] ? (pdfAttachments[selectedPdf].group ? `${pdfAttachments[selectedPdf].group} / ${pdfAttachments[selectedPdf].name}` : pdfAttachments[selectedPdf].name) : 'Document Viewer'}
                </h3>
              </div>
            </div>
            <div className="p-4 bg-gray-50 overflow-auto pdf-container" style={{ maxHeight: '70vh' }}>
              {renderAttachmentContent()}
            </div>
          </div>
        )}
        {/* Data Table (structured_po_items) */}
        {(viewMode === 'side-by-side' || viewMode === 'data-only') && structuredPoItems.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Mapped Data</h3>
                </div>
                <div className="flex space-x-2"></div>
              </div>
            </div>
            <div className="p-4 overflow-auto pdf-container" style={{ maxHeight: '70vh' }}>
              <div className="overflow-x-auto">
                <div className="grid gap-4">
                  {filterStructuredData(structuredPoItems).map((row, idx) => (
                    <div key={row._id?.value || idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      {structuredColumns.map((col) => {
                        const field = row[col];
                        if (!field || (field.value === null || field.value === undefined || field.value === '')) return null;
                        return (
                          <div key={col} className="mb-1">
                            <label className="block text-xs font-medium text-gray-500 mb-1">{col}</label>
                            <div className="text-sm px-3 py-1.5 rounded border text-gray-900 bg-white border-gray-100 flex items-center gap-2">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={
                                    isEditing && editedData[idx] && editedData[idx][col] && editedData[idx][col].value !== undefined
                                      ? editedData[idx][col].value
                                      : field.value !== undefined && field.value !== null
                                        ? field.value
                                        : ''
                                  }
                                  onChange={e => handleInputChange(idx, col, e.target.value)}
                                  className="border-0 bg-transparent text-sm w-full focus:ring-2 focus:ring-blue-500 focus:outline-none px-0 py-0"
                                />
                              ) : (
                                field.value !== undefined && field.value !== null ? String(field.value) : ''
                              )}
                              {!isEditing && field.confidence && !['order_number', 'commercial_invoice_number', 'commercial_invoice_date', 'cancelled'].includes(col) && (
                                <ConfidenceBadge confidence={field.confidence} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                {searchTerm && filterStructuredData(structuredPoItems).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No items found matching "{searchTerm}"
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderMappingTab = () => (
    <div className="space-y-6">
      {/* Combined Table */}
      {structuredPoItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center mb-2">
                <DocumentTextIcon className="h-5 w-5 mr-1" />
                <h2 className="text-lg font-semibold text-gray-900">Complete Mapping Details</h2>
              </div>
            </div>
            
            {/* Search input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-3 py-1.5 border border-gray-300 rounded-md text-sm w-48 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            {/* Card-based interface for mapping details */}
            <div className="space-y-4">
              {structuredPoItems.map((row, idx) => (
                <div key={row._id?.value || idx} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-blue-200">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                        <span className="text-sm font-semibold text-blue-700">{idx + 1}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 text-lg">
                        {row.itemCode?.value ? `Item Code: ${row.itemCode.value}` : `Item #${idx + 1}`} 
                        {row.pdf_path?.value && (
                          <button
                            onClick={() => {
                              const baseName = String(row.pdf_path.value).split(/[:/\\\\]/).pop();
                              const pdfIndex = pdfAttachments.findIndex(pdf => (pdf.name || '').split(/[:/\\\\]/).pop() === baseName);
                              if (pdfIndex !== -1) setSelectedPdf(pdfIndex);
                              setActiveTab('documents');
                            }}
                            className="ml-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-all duration-200 hover:shadow-sm group"
                            title="Click to view this document in Documents & Data tab"
                          >
                            <DocumentIcon className="h-3 w-3 mr-1 group-hover:scale-110 transition-transform" />
                            {row.pdf_path.value}
                            <ArrowLeftIcon className="h-3 w-3 ml-1 transform rotate-180 group-hover:translate-x-0.5 transition-transform" />
                          </button>
                        )}
                      </h4>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {structuredColumns.map((col) => {
                      const field = row[col];
                      if (!field || (field.value === null || field.value === undefined || field.value === '')) return null;
                      return (
                        <div key={col} className="group">
                          <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">{col}</label>
                          <div className="text-sm px-4 py-2.5 rounded-lg border text-gray-900 bg-white border-gray-200 flex items-center gap-2 shadow-sm group-hover:shadow-md transition-all duration-200 hover:border-blue-300">
                            {isEditing ? (
                              <input
                                type="text"
                                value={
                                  isEditing && editedData[idx] && editedData[idx][col] && editedData[idx][col].value !== undefined
                                    ? editedData[idx][col].value
                                    : field.value !== undefined && field.value !== null
                                      ? field.value
                                      : ''
                                }
                                onChange={e => handleInputChange(idx, col, e.target.value)}
                                className="border rounded px-2 py-1 text-sm w-full"
                              />
                            ) : (
                              field.value !== undefined && field.value !== null ? String(field.value) : ''
                            )}
                            {!isEditing && field.confidence && !['order_number', 'commercial_invoice_number', 'commercial_invoice_date', 'cancelled'].includes(col) && (
                              <ConfidenceBadge confidence={field.confidence} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
                
              {searchTerm && filterStructuredData(structuredPoItems).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No items found matching "{searchTerm}"
                </div>
              )}
            </div>
            
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/requests"
            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" /> Back
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Request #{requestId}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-start lg:justify-end">
          {!isEditing ? (
            <button
              onClick={handleEdit}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
            >
              <PencilSquareIcon className="h-4 w-4 mr-1" /> Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleSaveEdit}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                <CheckIcon className="h-4 w-4 mr-1" /> Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-400 hover:bg-gray-500 rounded-md"
              >
                <XMarkIcon className="h-4 w-4 mr-1" /> Cancel
              </button>
            </>
          )}
          <button
            onClick={handleSendExcelFile}
            disabled={loadingSendExcel}
            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-md ${
              loadingSendExcel ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loadingSendExcel ? (
              <>
                <SmallSpinner className="h-4 w-4 mr-2" /> Sending...
              </>
            ) : (
              <>
                <ShareIcon className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Send Excel</span>
              </>
            )}
          </button>
          <button
            onClick={handleDownloadExcelFile}
            disabled={loadingDownloadExcel}
            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-md ${
              loadingDownloadExcel ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loadingDownloadExcel ? (
              <>
                <SmallSpinner className="h-4 w-4 mr-2" /> Downloading...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Download Excel</span>
              </>
            )}
          </button>
          <button
            onClick={handleDownloadXmlFile}
            disabled={loadingDownloadXml}
            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-md ${
              loadingDownloadXml ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {loadingDownloadXml ? (
              <>
                <SmallSpinner className="h-4 w-4 mr-2" /> Downloading...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Download XML</span>
              </>
            )}
          </button>
          <button
            onClick={handleSendXmlToFtp}
            disabled={loadingSendXml}
            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-md ${
              loadingSendXml ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {loadingSendXml ? (
              <>
                <SmallSpinner className="h-4 w-4 mr-2" /> Sending...
              </>
            ) : (
              <>
                <DocumentTextIcon className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Send to Smart Order</span>
              </>
            )}
          </button>
        </div>
      </div>

      {(isReprocessWaiting || latestAttempt || data?.is_reprocess) && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-indigo-900">
                {latestAttempt
                  ? `New attempt created: #${latestAttempt.request_id}`
                  : isReprocessWaiting
                    ? 'Reprocess queued'
                    : `Attempt ${data?.attempt_number || 1}`}
              </div>
              <div className="mt-1 text-sm text-indigo-800">
                {latestAttempt
                  ? `Attempt ${latestAttempt.attempt_number || ''} is linked to this request.`
                  : isReprocessWaiting
                    ? 'The email worker will create a fresh request ID when it picks this up.'
                    : data?.parent_request_id
                      ? `Reprocessed from #${data.parent_request_id}.`
                      : `Original request #${data?.root_request_id || requestId}.`}
              </div>
            </div>
            {latestAttempt && (
              <button
                type="button"
                onClick={() => navigate(`/requests/${latestAttempt.request_id}`)}
                className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
              >
                View Attempt
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'documents' && renderDocumentsTab()}
        {activeTab === 'mapping' && renderMappingTab()}
      </div>
      </div>
    </div>
  );
};

export default RequestDetail; 
